<template>
  <v-container fluid fill-height class="error-container">
    <!-- Animated background elements -->
    <div class="floating-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
      <div class="shape shape-4"></div>
    </div>

    <!-- Main content -->
    <div class="error-content">
      <!-- 404 Animation -->
      <div class="error-number-container">
        <div class="error-digit">4</div>
        <div class="error-digit-middle">
          <div class="error-zero">
            <div class="zero-inner"></div>
          </div>
        </div>
        <div class="error-digit">4</div>
      </div>

      <!-- Glitch effect text -->
      <h1 class="error-title" data-text="OOPS! PAGE NOT FOUND">
        OOPS! PAGE NOT FOUND
      </h1>

      <!-- Subtitle with typing animation -->
      <p class="error-subtitle">
        <span class="typing-text">{{ typingText }}</span>
        <span class="cursor" :class="{ 'blink': showCursor }">|</span>
      </p>

      <!-- Action buttons -->
      <div class="action-buttons">
        <v-btn
          class="primary-btn"
          elevation="0"
          large
          @click="goHome"
        >
          <v-icon left>mdi-home</v-icon>
          Take Me Home
        </v-btn>
        
        <v-btn
          class="secondary-btn"
          elevation="0"
          large
          outlined
          @click="goBack"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Go Back
        </v-btn>
      </div>

      <!-- Additional help -->
      <div class="help-section">
        <p class="help-text">Need help? Try these suggestions:</p>
        <div class="suggestion-chips">
          <v-chip
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="suggestion-chip"
            small
            @click="handleSuggestion(suggestion)"
          >
            {{ suggestion }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Particle effect canvas -->
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>
  </v-container>
</template>

<script>
export default {
  name: 'Error404Page',
  data() {
    return {
      typingText: '',
      fullText: 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
      showCursor: true,
      suggestions: ['Home'],
      particles: [],
      animationId: null
    }
  },
  mounted() {
    this.startTypingAnimation()
    this.initParticles()
    this.startCursorBlink()
  },
  beforeDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
  },
  methods: {
    goHome() {
      this.$router.push({ name: 'home' })
    },
    goBack() {
      this.$router.go(-1)
    },
    handleSuggestion(suggestion) {
      // Handle suggestion clicks
      this.$router.push({ name: suggestion.toLowerCase() })
    },
    startTypingAnimation() {
      let i = 0
      const typeWriter = () => {
        if (i < this.fullText.length) {
          this.typingText += this.fullText.charAt(i)
          i++
          setTimeout(typeWriter, 50)
        }
      }
      setTimeout(typeWriter, 1000)
    },
    startCursorBlink() {
      setInterval(() => {
        this.showCursor = !this.showCursor
      }, 500)
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      // Create particles
      for (let i = 0; i < 50; i++) {
        this.particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 1,
          speedX: (Math.random() - 0.5) * 2,
          speedY: (Math.random() - 0.5) * 2,
          opacity: Math.random() * 0.5 + 0.2
        })
      }

      this.animateParticles(ctx, canvas)
    },
    animateParticles(ctx, canvas) {
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        this.particles.forEach(particle => {
          particle.x += particle.speedX
          particle.y += particle.speedY

          if (particle.x > canvas.width) particle.x = 0
          if (particle.x < 0) particle.x = canvas.width
          if (particle.y > canvas.height) particle.y = 0
          if (particle.y < 0) particle.y = canvas.height

          ctx.globalAlpha = particle.opacity
          ctx.fillStyle = '#64ffda'
          ctx.beginPath()
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
          ctx.fill()
        })

        this.animationId = requestAnimationFrame(animate)
      }
      animate()
    }
  }
}
</script>

<style scoped>
.error-container {
 background: linear-gradient(135deg, #0f0f23 0%, #3B7CA1 50%, #16213e 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

/* Floating background shapes */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(100, 255, 218, 0.1);
  animation: float 20s infinite ease-in-out;
}

.shape-1 {
  width: 80px;
  height: 80px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 120px;
  height: 120px;
  top: 70%;
  right: 10%;
  animation-delay: -5s;
}

.shape-3 {
  width: 60px;
  height: 60px;
  top: 40%;
  left: 80%;
  animation-delay: -10s;
}

.shape-4 {
  width: 100px;
  height: 100px;
  bottom: 20%;
  left: 20%;
  animation-delay: -15s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-30px) rotate(120deg);
  }
  66% {
    transform: translateY(20px) rotate(240deg);
  }
}

/* Main content */
.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 2;
  position: relative;
  padding: 2rem;
  margin: auto;
}

/* 404 Number Animation */
.error-number-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.error-digit {
  font-size: 8rem;
  font-weight: 900;
  color: #64ffda;
  text-shadow: 0 0 30px rgba(100, 255, 218, 0.5);
  animation: pulse 2s ease-in-out infinite alternate;
}

.error-digit-middle {
  margin: 0 1rem;
}

.error-zero {
  width: 120px;
  height: 120px;
  border: 8px solid #64ffda;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: rotate 10s linear infinite;
}

.zero-inner {
  width: 60px;
  height: 60px;
  border: 4px solid #ff6b6b;
  border-radius: 50%;
  animation: rotate 5s linear infinite reverse;
}

@keyframes pulse {
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Glitch effect title */
.error-title {
  font-size: 3rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1rem;
  position: relative;
  animation: glitch 2s infinite;
}

.error-title::before,
.error-title::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.error-title::before {
  animation: glitch-1 0.5s infinite;
  color: #ff6b6b;
  z-index: -1;
}

.error-title::after {
  animation: glitch-2 0.5s infinite;
  color: #64ffda;
  z-index: -2;
}

@keyframes glitch {
  0%, 100% { transform: translate(0); }
  20% { transform: translate(-2px, 2px); }
  40% { transform: translate(-2px, -2px); }
  60% { transform: translate(2px, 2px); }
  80% { transform: translate(2px, -2px); }
}

@keyframes glitch-1 {
  0%, 100% { transform: translate(0); }
  10% { transform: translate(-2px, -2px); }
  20% { transform: translate(2px, 2px); }
}

@keyframes glitch-2 {
  0%, 100% { transform: translate(0); }
  30% { transform: translate(3px, 3px); }
  40% { transform: translate(-3px, -3px); }
}

/* Typing animation */
.error-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  max-width: 600px;
  line-height: 1.6;
}

.typing-text {
  display: inline;
}

.cursor {
  display: inline-block;
  color: #64ffda;
  font-weight: bold;
}

.cursor.blink {
  opacity: 0;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.primary-btn {
  background: linear-gradient(45deg, #64ffda, #4fd3a7) !important;
  color: #0f0f23 !important;
  font-weight: 600;
  text-transform: none;
  border-radius: 25px;
  padding: 0 30px;
  transition: all 0.3s ease;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(100, 255, 218, 0.3);
}

.secondary-btn {
  border: 2px solid #64ffda !important;
  color: #64ffda !important;
  font-weight: 600;
  text-transform: none;
  border-radius: 25px;
  padding: 0 30px;
  transition: all 0.3s ease;
}

.secondary-btn:hover {
  background: rgba(100, 255, 218, 0.1) !important;
  transform: translateY(-2px);
}

/* Help section */
.help-section {
  max-width: 500px;
}

.help-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.suggestion-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.suggestion-chip {
  background: rgba(100, 255, 218, 0.1) !important;
  color: #64ffda !important;
  border: 1px solid rgba(100, 255, 218, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.suggestion-chip:hover {
  background: rgba(100, 255, 218, 0.2) !important;
  transform: translateY(-1px);
}

/* Particle canvas */
.particle-canvas {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
}

/* Responsive design */
@media (max-width: 768px) {
  .error-digit {
    font-size: 4rem;
  }
  
  .error-zero {
    width: 80px;
    height: 80px;
  }
  
  .zero-inner {
    width: 40px;
    height: 40px;
  }
  
  .error-title {
    font-size: 2rem;
  }
  
  .error-subtitle {
    font-size: 1rem;
    padding: 0 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
  
  .error-content {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .error-digit {
    font-size: 3rem;
  }
  
  .error-zero {
    width: 60px;
    height: 60px;
  }
  
  .zero-inner {
    width: 30px;
    height: 30px;
  }
  
  .error-title {
    font-size: 1.5rem;
  }
  
  .shape {
    display: none; /* Hide floating shapes on very small screens */
  }
}

/* Dark theme compatibility */
.theme--dark .error-container {
  background: linear-gradient(135deg, #121212 0%, #1e1e1e 50%, #2d2d2d 100%);
}
</style>